// ─────────────────────────────────────────────────────────────────────────────
// DashboardSnapshotProvider.js
// Custom React hook that fetches /api/reports/snapshot with a 400ms timeout
// guard (spec §3.4 SNAPSHOT_PRIME_TIMEOUT_MS). Non-blocking: if the API is
// slow the sweep proceeds with null snapshot. Results are module-cached for
// 30 s so re-mounts don't re-fetch during the same session.
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useEffect, useRef } from 'react';
import ApiCall from '../../utils/apicall';

const SNAPSHOT_PRIME_TIMEOUT_MS = 400;
const CACHE_TTL_MS              = 30_000;

// Module-level cache — survives re-mounts within a session
let _cachedSnapshot = null;
let _cacheTimestamp = 0;

function isCacheFresh() {
  return _cachedSnapshot !== null && Date.now() - _cacheTimestamp < CACHE_TTL_MS;
}

/**
 * useDashboardSnapshot()
 * @returns {{ snapshot: object|null, loading: boolean }}
 *
 * snapshot matches DashboardSnapshot schema from spec §3.3:
 *   openNcrs, qcHolds, materialShortages, loadsInProgress,
 *   receivingPct, issuedPct, fabricationPct, qcPassPct,
 *   preTraceEnabled, unlinkedPieceCount, missingMtrCount, overweightLoadBlocked
 */
export function useDashboardSnapshot() {
  const [snapshot, setSnapshot] = useState(isCacheFresh() ? _cachedSnapshot : null);
  const [loading,  setLoading]  = useState(!isCacheFresh());
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    // Cache hit — no network needed
    if (isCacheFresh()) {
      setSnapshot(_cachedSnapshot);
      setLoading(false);
      return;
    }

    // Prime timeout: don't block the overlay for a slow API
    const primeTimer = setTimeout(() => {
      if (mountedRef.current) setLoading(false);
    }, SNAPSHOT_PRIME_TIMEOUT_MS);

    ApiCall
      .get('/reports/snapshot')
      .then(({ data }) => {
        if (!mountedRef.current) return;
        const s = data?.data ?? null;
        _cachedSnapshot = s;
        _cacheTimestamp = Date.now();
        setSnapshot(s);
      })
      .catch(() => {
        // Non-blocking failure — proceed without snapshot data
        // Overlay will resolve to 'ready' / 'loading' state
      })
      .finally(() => {
        clearTimeout(primeTimer);
        if (mountedRef.current) setLoading(false);
      });

    return () => {
      mountedRef.current = false;
      clearTimeout(primeTimer);
    };
  }, []);

  return { snapshot, loading };
}

/**
 * invalidateSnapshotCache()
 * Call this after any NCR create/close or heat update to force a fresh fetch.
 */
export function invalidateSnapshotCache() {
  _cachedSnapshot = null;
  _cacheTimestamp = 0;
}
